// ─── cst-quote-request.js ────────────────────────────────────────────────
// Handles the HydraPro quote request flow.
// Collects form + cart data and posts it with a Cloudflare Turnstile token
// to the form Worker (/quote), which verifies the token, creates a Shopify
// Draft Order, sends the confirmation email and forwards the quote to Zapier.
// No Storefront API, no checkout flow, no discount code.
// Worker URL + translated error messages come from window.SHOP_DATA
// (sections/cst-main-cart-items.liquid).

// ── Main flow ──────────────────────────────────────────────────────────

async function handleQuoteSubmission() {
  // 1. Collect and validate form data
  const formData = collectFormData();

  // 2. Get the Turnstile token (widget in the quote form panel)
  const turnstileToken = CST.Turnstile.getToken(document.querySelector('.cst-quote-turnstile'));
  if (!turnstileToken) throw new Error(quoteMessage('verification_required'));

  // 3. Get current cart contents
  const cartData = await getCartData();

  if (cartData.items.length === 0) {
    throw new Error('Your cart is empty. Please add products before requesting a quote.');
  }

  // 4. Submit to Cloudflare Worker — Worker verifies the token, creates the
  //    Draft Order, sends the email and forwards the quote to Zapier
  await submitToWorker(formData, cartData, turnstileToken);

  // 5. Clear the cart
  await clearCart();

  // custom.js will call setStep('success') to show the success panel
}

// ── Form data collection ───────────────────────────────────────────────

function collectFormData() {
  const get = (id) => document.getElementById(id)?.value.trim() ?? '';

  const data = {
    firstName: get('first_name'),
    lastName: get('last_name'),
    company: get('company_name'),
    email: get('email'),
    phone: get('phone'),
    country: get('country'),
    address: get('address'),
    message: get('comments'),
  };

  if (!data.company) throw new Error('Company name is required.');
  if (!data.email || !/\S+@\S+\.\S+/.test(data.email)) {
    throw new Error('A valid email address is required.');
  }

  return data;
}

// ── Cart data ──────────────────────────────────────────────────────────

async function getCartData() {
  const res = await fetch('/cart.js');
  if (!res.ok) throw new Error('Could not read cart contents. Please refresh and try again.');
  return res.json();
}

// ── Worker submission ──────────────────────────────────────────────────

async function submitToWorker(formData, cartData, turnstileToken) {
  const payload = {
    turnstileToken,
    pageUrl: window.location.href,
    customer: {
      firstName: formData.firstName,
      lastName: formData.lastName,
      company: formData.company,
      email: formData.email,
      phone: formData.phone,
      country: formData.country,
      address: formData.address,
    },
    lineItems: cartData.items.map((item) => ({
      variantId: item.variant_id, // plain integer — Worker sends directly to Admin API
      quantity: item.quantity,
      title: item.title,
      properties: item.properties?._is_price_hidden === 'true'
        ? [{ name: '_is_price_hidden', value: 'true' }]
        : []
    })),
    note: formData.message,
  };

  try {
    await CST.FormBackend.post(`${window.SHOP_DATA.workerUrl}/quote`, payload);
  } catch (err) {
    console.error('[CST] Quote submission failed:', err.message);
    // 400 = server-side validation message; everything else gets a translated message.
    if (err.status === 400) throw new Error(err.message);
    throw new Error(quoteMessage(err.code));
  }
}

// Translated submit message by Worker error code (see CST.FormBackend).
function quoteMessage(code) {
  const messages = window.SHOP_DATA?.formMessages || {};
  return messages[code] || messages.failed || 'Quote submission failed. Please try again or contact us directly.';
}

// ── Cart clear ─────────────────────────────────────────────────────────

async function clearCart() {
  try {
    await fetch('/cart/clear.js', { method: 'POST' });
  } catch {
    // Non-critical — don't throw if cart clear fails
    console.warn('Cart clear failed — user may need to clear manually.');
  }
}
